import { useDateCalendarMode } from "../../hooks/useDateCalendarMode";
import { BsDatePicker } from "./BsDatePicker";
import { EnglishDatePicker, EnglishDatePickerProps } from "./EnglishDatePicker";

export interface CalendarDateInputProps
  extends Omit<EnglishDatePickerProps, "onChange" | "selected"> {
  selected?: Date | null;
  onChange: (date: Date | null) => void;
  /** When true and the BS calendar mode is active, the date input can also be typed into directly. No-op in AD mode, which is already typeable. */
  allowTyping?: boolean;
}

/**
 * Drop-in replacement for `EnglishDatePicker` that renders the BS calendar
 * instead of the AD one when the global AD/BS preference (set once, e.g. in
 * the navbar) is "BS". The `selected`/`onChange` contract stays Date-based
 * and AD-only either way — BS is purely a display/input convenience, never
 * the value callers receive.
 */
export function CalendarDateInput({
  selected,
  onChange,
  minDate,
  maxDate,
  size,
  disabled,
  allowTyping,
  ...props
}: CalendarDateInputProps) {
  const [mode] = useDateCalendarMode();

  if (mode === "BS") {
    return (
      <BsDatePicker
        selected={selected}
        onChange={onChange}
        size={size}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        placeholder={props.placeholderText}
        allowTyping={allowTyping}
      />
    );
  }

  return (
    <EnglishDatePicker
      {...props}
      size={size}
      disabled={disabled}
      selected={selected}
      minDate={minDate}
      maxDate={maxDate}
      onChange={(date) => onChange(date as Date | null)}
    />
  );
}
